import { Component, inject, OnInit, signal, ViewChild } from '@angular/core';
import { SessionContext } from '../../../../../../core/services/config/app.settings';
import { OperateursService } from '../../../../../../data/intervenant/services/operateurs/operateurs.service';
import { Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { SuiviEmailsService } from '../../../../../../data/mail/services/suivi-emails.service';
import { MailSummary } from '../../../../../../data/mail/models/mail-summary.model';
import { UtilisateursService } from '../../../../../../data/intervenant/services/utilisateurs/utilisateurs.service';
import { Utilisateur } from '../../../../../../data/intervenant/models/utilisateur.model';
import { OperateurExport } from '../../../../../../data/edition/models/operateur-export.model';
import { Table, TableModule } from 'primeng/table';
import { TranslateModule } from '@ngx-translate/core';
import { MultiSelectModule } from 'primeng/multiselect';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DatePipe, NgClass } from '@angular/common';
import { ChipModule } from 'primeng/chip';
import { StyleClass } from 'primeng/styleclass';
import { MailStatutLibelle } from '../../../../../../data/mail/models/mail-statuts.model';
import { HistoriqueStatutsComponent } from './modals/historique-statuts/historique-statuts.component';
import { InfosStatutsComponent } from './modals/infos-statuts/infos-statuts.component';
import { LoaderService } from '../../../../../../core/services/loader/loader.service';
import { Column } from '../../../../../../shared/models/column.model';

@Component({
    selector: 'app-suivi-emails',
    templateUrl: './suivi-emails.component.html',
    styleUrls: ['./suivi-emails.component.scss'],
    imports: [
        TableModule,
        TranslateModule,
        MultiSelectModule,
        FormsModule,
        ButtonModule,
        ChipModule,
        NgClass,
        StyleClass,
        HistoriqueStatutsComponent,
        InfosStatutsComponent
    ],
    standalone: true
})
export class SuiviEmailsComponent implements OnInit {
    /**
     * Properties
     */
    mails: MailSummary[] = [];
    mailsStatus: { name: string; code: string }[] = [];
    columns: Column[] = [
        { field: 'date', label: "Date d'envoi", filter: 'date', width: '25%' },
        { field: 'destinataire', label: 'Destinataire', filter: 'text', width: '25%' },
        { field: 'objet', label: 'Objet', filter: 'text', width: '25%' },
        { field: 'dernierStatutCode', label: 'Dernier statut', filter: 'status', width: '25%' }
    ];

    @ViewChild('dt') dt: Table;
    protected readonly MailStatutLibelle = MailStatutLibelle;

    /**
     * Signals
     */
    showHistoriqueStatuts = signal(false);
    selectedMail = signal<MailSummary | undefined>(undefined);
    showInfosStatuts = signal(false);

    /**
     * Dependencies injections
     */
    private readonly operateursService = inject(OperateursService);
    private readonly suiviEmailsService = inject(SuiviEmailsService);
    private readonly utilisateursService = inject(UtilisateursService);
    private readonly datePipe = inject(DatePipe);
    private readonly loaderService = inject(LoaderService);

    /**
     * Getters
     */
    public get isAnpp(): boolean {
        return SessionContext.get('codeDomaine') === 'ANPP';
    }

    private get idOrganisme(): number {
        return SessionContext.get('idOrganisme');
    }

    /**
     * On init component
     */
    ngOnInit(): void {
        
        this.getOperateurs()
            .pipe(
                switchMap(operateurs => {
                    const operateursIds = operateurs.map(op => op.id);
                    return this.suiviEmailsService.getMailsByIntervenants(operateursIds);
                }),
                this.loaderService.operator()
            )
            .subscribe({
                next: mails => {
                    this.mails = mails.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
                    this.mailsStatus = [...new Set(mails.map(mail => mail.dernierStatutCode).sort((a, b) => a.localeCompare(b)))].map(status => ({
                        name: MailStatutLibelle[status],
                        code: status
                    }));
                }
            });
    }

    /**
     * Retourne les opérateurs selon le contexte ANPP ou VIN
     * @private
     */
    private getOperateurs(): Observable<Utilisateur[] | OperateurExport[]> {
        return this.isAnpp ? this.utilisateursService.getUtilisateurs() : this.operateursService.getOperateursByOrganisme(this.idOrganisme);
    }

    /**
     * Retourne la valeur d'une cellule du tableau de suivi des emails
     * @param mail - MailSummary
     * @param field - Column name
     */
    getCellValue(mail: MailSummary, field: string): string | number | undefined {
        return field === 'date' ? this.datePipe.transform(mail[field], 'dd/MM/yyyy') : mail[field];
    }

    /**
     * Ouvre le modal d'historique des statuts pour un mail donné
     * @param mail - MailSummary
     */
    openHistorique(mail: MailSummary): void {
        this.selectedMail.set(mail);
        this.showHistoriqueStatuts.set(true);
    }

    onHistoriqueVisibleChange(visible: boolean): void {
        this.showHistoriqueStatuts.set(visible);

        if (!visible) {
            this.selectedMail.set(undefined);
        }
    }

    /**
     * Ouvre le modal d'informations sur les statuts
     */
    openInfos(): void {
        this.showInfosStatuts.set(true);
    }
}
